import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import ServiceBody from "../components/site/ServiceBody";
import FAQBlock from "../components/site/FAQBlock";
import CostFactors from "../components/site/CostFactors";
import ProcessSteps from "../components/site/ProcessSteps";
import useSEO from "../hooks/useSEO";
import {
  DoorOpen,
  KeyRound,
  Scan,
  Phone,
  UserCheck,
  Fingerprint,
  Smartphone,
  Cog,
  Building2,
} from "lucide-react";

const IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/867rgo9t_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_26_56%20PM.png";

export default function AccessControl() {
  useSEO({
    title: "Access Control & Entry Systems Ireland | Doors, Gates & Intercoms | FSS",
    description:
      "Secure and scalable access control, intercom and entry management systems for commercial premises across Ireland. Design, installation, maintenance and multi-site support.",
    canonicalPath: "/access-control",
  });

  return (
    <>
      <PageHero
        eyebrow="Access Control & Entry Systems"
        title="Control who enters your premises."
        subtitle="Secure and scalable access control, intercom and entry management systems for doors, gates, barriers, turnstiles and restricted areas. Solutions are tailored to the building, number of users, required security level and existing infrastructure."
        image={IMG}
        imageAlt="Access control keypad being used at a commercial entry point"
        ctaPrimary={{ to: "/contact", label: "Request an Access Control Survey" }}
        ctaSecondary={{ to: "/contact", label: "Discuss Your Entry Requirements" }}
      />

      <ServiceBody
        includedSubtitle="Access control services"
        included={[
          { icon: DoorOpen, title: "Access Control Systems", copy: "Door, gate, barrier and turnstile access. Keypad, card, fob, mobile and biometric options with time-based permissions and audit trails." },
          { icon: KeyRound, title: "Card, Fob & Mobile Access", copy: "Proximity cards, key fobs and mobile credentials with instant activation, removal and lost-credential management." },
          { icon: Fingerprint, title: "Biometric Access (where offered)", copy: "Fingerprint and facial recognition options with dual authentication, restricted area access and audit trails." },
          { icon: Phone, title: "Intercom Systems", copy: "Audio, video and smartphone-enabled intercoms for offices, apartments and multi-unit sites." },
          { icon: UserCheck, title: "Visitor & Entry Management", copy: "Visitor verification, temporary and contractor access, reception-controlled entry and remote door release." },
          { icon: Cog, title: "Gate & Barrier Automation (where offered)", copy: "Automated gates and vehicle barriers integrated with access control, intercom and CCTV." },
          { icon: Building2, title: "Multi-Door & Multi-Site", copy: "Central user management, permissions and reporting across multiple doors, buildings and sites." },
          { icon: Smartphone, title: "Integrated Solutions", copy: "Access control integrated with CCTV, intruder alarms and monitoring for a coherent security ecosystem." },
        ]}
        whoFor={[
          "Multi-tenant commercial buildings",
          "Managed estates and property portfolios",
          "Industrial sites with restricted zones",
          "Schools, healthcare and public-sector facilities",
          "Reception-controlled offices",
          "Sites with vehicle gates and barriers",
        ]}
        benefits={[
          "Improve security and reduce risk of unauthorised access",
          "Track and audit entry activity",
          "Streamline visitor and contractor management",
          "Scale users, doors and sites as needs change",
          "Integrate with CCTV and monitoring for coherent security",
        ]}
        defaultService="Door Access Control"
      />

      <section id="intercoms" className="fss-section bg-white">
        <div className="fss-container">
          <div className="max-w-2xl mb-10">
            <div className="fss-overline">Intercoms</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Audio, video or smartphone intercoms
            </h2>
            <p className="mt-3 text-slate-600">
              The right intercom depends on the building type, visitor flow and
              how you want to verify and release access.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Audio intercoms", copy: "Traditional voice-only intercoms suited to smaller buildings and simple entry points." },
              { title: "Video intercoms", copy: "Visual verification of visitors, useful in offices, receptions and multi-unit buildings." },
              { title: "Smartphone intercoms", copy: "App-connected intercoms enabling remote verification and door release from a mobile device." },
            ].map((c, i) => (
              <div key={i} className="fss-card">
                <h3 className="font-display text-lg font-semibold text-slate-900">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="biometric" className="fss-section bg-slate-50 border-y border-slate-200">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="fss-overline">Biometric access</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Biometric options require careful setup and data handling.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-slate-600 leading-relaxed">
            <p>
              Where offered, biometric access — including fingerprint or facial
              recognition — can be used to restrict entry to high-security
              areas. Options may include dual authentication with a card or fob.
            </p>
            <p>
              Biometric systems involve personal data, so appropriate consent,
              policies, access management and data-protection considerations are
              important. Deploying a biometric system does not automatically
              make a business compliant with data protection requirements — the
              wider policies and controls of the organisation are also relevant.
            </p>
          </div>
        </div>
      </section>

      <ProcessSteps
        eyebrow="How it works"
        title="Access control installation process"
        steps={[
          { title: "Site Assessment", copy: "We review the doors, gates and entry points across the premises." },
          { title: "User & Security Requirements", copy: "We understand who needs to enter where, and how access should be controlled." },
          { title: "System Recommendation", copy: "A clear proposal matched to your building and users." },
          { title: "Quotation", copy: "An itemised quotation covering equipment, installation and any integrations." },
          { title: "Installation & Configuration", copy: "System is installed, credentials issued and permissions configured." },
          { title: "Testing & Handover", copy: "Full testing, user setup and handover documentation." },
        ]}
      />

      <CostFactors
        title="What affects access control cost"
        factors={[
          "Number of doors, gates or entry points",
          "Number of users to be managed",
          "Credential type (keypad, card, fob, mobile, biometric)",
          "Condition and construction of existing doors",
          "Cabling and network requirements",
          "Reporting and audit requirements",
          "Integrations with CCTV, intruder alarms or intercoms",
          "Gate or barrier requirements",
          "Multi-site coordination and central management",
        ]}
      />

      <FAQBlock
        eyebrow="Access Control FAQ"
        title="Buyer questions"
        items={[
          { q: "What type of access control is suitable for my premises?", a: "It depends on the number of users, doors, security requirements and integration needs. We recommend the appropriate option following a site assessment." },
          { q: "What is the difference between cards, fobs, keypads and biometrics?", a: "Cards and fobs are widely used credentials. Keypads use a PIN. Biometrics (where offered) use fingerprint or facial recognition, often with a second factor." },
          { q: "Can access control be added to an existing door?", a: "In most cases yes — subject to the door construction and any existing lock or hardware." },
          { q: "Can access control be added to gates and barriers?", a: "Yes — where offered, we integrate access control with automated gates, vehicle barriers and intercoms." },
          { q: "Can access permissions be changed remotely?", a: "Modern systems support central management of users, credentials and permissions." },
          { q: "What happens during a power cut?", a: "Access control systems typically have battery back-up and defined behaviours (fail-safe or fail-secure) — we design this to match your building requirements." },
          { q: "Can the system record who entered the building?", a: "Yes — most systems maintain an audit trail of entry and exit events." },
          { q: "Can access control integrate with CCTV?", a: "Yes — event-driven CCTV and access control integration is common." },
          { q: "Can access control integrate with an intercom?", a: "Yes — audio, video and smartphone intercoms can trigger door and gate release." },
          { q: "Can FSS support multiple doors or sites?", a: "Yes — from a single door to multi-site portfolios with central management." },
          { q: "What happens when a card or fob is lost?", a: "It is deactivated in the system and a replacement issued — no locks need to be changed." },
          { q: "What affects the cost?", a: "The number of doors and users, credential type, existing doors, cabling, network access, reporting and integrations." },
          { q: "Can an existing system be upgraded?", a: "In many cases yes — including panel upgrades, new credentials or additional doors." },
        ]}
        bg="slate"
      />

      <CTABanner
        title="Control who enters your premises."
        subtitle="Contact FSS to arrange an access control or entry system assessment."
        primary={{ to: "/contact", label: "Request an Access Control Survey" }}
        secondary={{ to: "/contact", label: "Discuss Your Entry Requirements" }}
      />
    </>
  );
}
